import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2, Loader2, ShieldAlert, Trash2, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { PageGuard } from '@components/PageGuard';
import {
  deletionRequestsService,
  deletionStatusChip,
  type DeletionRequest,
  type DeletionRequestStatus,
} from '@services/deletionRequests.service';
import { usePermission } from '@hooks/usePermission';
import { cn } from '@lib/utils';
import { formatTimestamp, humanizeCode, shortId } from '../finance/ledgerFormat';

const FILTERS: { value: DeletionRequestStatus; label: string }[] = [
  { value: 'pending', label: 'Awaiting review' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'cancelled', label: 'Cancelled by holder' },
];

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const e = err as { message?: unknown };
    if (typeof e.message === 'string' && e.message.trim()) return e.message;
  }
  return fallback;
}

/** Where the request came from, in a few words. */
function sourceText(r: DeletionRequest): string {
  if (r.source === 'web') return 'Website form';
  return r.app ? `${humanizeCode(r.app)} app` : 'App (older version)';
}

/** Days a pending request has been waiting, for the promise made in the apps. */
function waitingDays(requestedAt: string): number {
  const ms = Date.now() - new Date(requestedAt).getTime();
  return Math.max(0, Math.floor(ms / 86_400_000));
}

/**
 * ACCOUNT DELETION REQUESTS. People who asked Chekit to delete their account.
 *
 * The account was locked the moment the request was filed and every other
 * session was revoked; nothing has been erased. Approving runs the same
 * delete as the Users page (the person's name, phone and email are
 * anonymised; order and payment records stay for tax) and cannot be undone.
 * Rejecting puts the account back as it was and sends the holder the note,
 * so it has to say what they need to do.
 *
 * The apps tell the holder a review usually takes up to 7 days. The waiting
 * column is here so that promise is kept.
 */
export const DeletionRequestsPage = () => {
  const queryClient = useQueryClient();
  const { hasCheKitPermission } = usePermission();
  const canDecide = hasCheKitPermission('users.delete');

  const [status, setStatus] = useState<DeletionRequestStatus>('pending');
  const [active, setActive] = useState<DeletionRequest | null>(null);
  const [note, setNote] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const { data, isLoading, isError, error: loadError } = useQuery({
    queryKey: ['deletion-requests', status],
    queryFn: () => deletionRequestsService.list(status),
  });
  const rows = useMemo(() => data ?? [], [data]);
  const overdue = status === 'pending' ? rows.filter((r) => waitingDays(r.requestedAt) >= 7).length : 0;

  const open = (r: DeletionRequest) => {
    setActive(r);
    setNote('');
    setConfirmed(false);
    setError(null);
  };

  const done = (message: string) => {
    toast.success(message);
    setActive(null);
    queryClient.invalidateQueries({ queryKey: ['deletion-requests'] });
    queryClient.invalidateQueries({ queryKey: ['users'] });
  };

  const approveM = useMutation({
    mutationFn: ({ id, n }: { id: string; n: string }) => deletionRequestsService.approve(id, n),
    onSuccess: () => done('Approved. The account has been deleted and its apps signed out.'),
    onError: (e) => setError(apiMessage(e, 'Could not approve.')),
  });
  const rejectM = useMutation({
    mutationFn: ({ id, n }: { id: string; n: string }) => deletionRequestsService.reject(id, n),
    onSuccess: () => done('Rejected. The account is unlocked and the holder has been sent your note.'),
    onError: (e) => setError(apiMessage(e, 'Could not reject.')),
  });
  const busy = approveM.isPending || rejectM.isPending;

  return (
    <PageGuard permission="users.read">
      <div className="space-y-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="font-display text-[32px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
              Deletion requests
            </h1>
            <p className="mt-1 max-w-2xl text-[14px] text-[var(--text-muted)]">
              People who asked us to delete their account, from the apps or the website. Each account is
              locked until you decide. Approving erases the person&apos;s name and contact details and cannot
              be undone. Rejecting unlocks the account and sends them your note.
            </p>
          </div>
          {status === 'pending' && rows.length > 0 && (
            <div className="rounded-3xl bg-[var(--bg-surface)] px-6 py-4 shadow-soft">
              <div className="text-[13px] text-[var(--text-muted)]">Waiting for a decision</div>
              <div className="font-display mt-1 text-[28px] font-medium leading-none tracking-[-0.03em] text-[var(--text-primary)]">
                {rows.length}
              </div>
              <div className={cn('mt-1 text-[12px]', overdue > 0 ? 'text-[var(--state-danger)]' : 'text-[var(--text-muted)]')}>
                {overdue > 0 ? `${overdue} waiting 7 days or more` : 'none past 7 days'}
              </div>
            </div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.value}
              type="button"
              onClick={() => setStatus(f.value)}
              className={cn(
                'rounded-full px-4 py-2 text-[13px] font-semibold transition-colors',
                status === f.value
                  ? 'bg-[var(--text-primary)] text-[var(--bg-surface)]'
                  : 'bg-[var(--bg-surface)] text-[var(--text-secondary)] hover:bg-[var(--bg-base)]',
              )}
            >
              {f.label}
            </button>
          ))}
        </div>

        <div className="overflow-hidden rounded-3xl bg-[var(--bg-surface)] shadow-soft">
          {isLoading && (
            <div className="flex items-center gap-2 p-12 text-[14px] text-[var(--text-muted)]">
              <Loader2 className="h-4 w-4 animate-spin" strokeWidth={1.5} /> Loading requests…
            </div>
          )}
          {isError && (
            <div className="p-12 text-center text-[14px] text-[var(--state-danger)]">
              {apiMessage(loadError, 'Could not load deletion requests.')}
            </div>
          )}
          {!isLoading && !isError && rows.length === 0 && (
            <div className="p-12 text-center text-[14px] text-[var(--text-muted)]">
              {status === 'pending' ? 'No requests waiting. Nothing to review.' : 'Nothing with that status.'}
            </div>
          )}
          {rows.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-[14px]">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-[0.06em] text-[var(--text-muted)]">
                    <th className="px-6 py-4 font-semibold">Account</th>
                    <th className="px-6 py-4 font-semibold">From</th>
                    <th className="px-6 py-4 font-semibold">Requested</th>
                    <th className="px-6 py-4 font-semibold">{status === 'pending' ? 'Can it be approved?' : 'Status'}</th>
                    <th className="px-6 py-4" />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => {
                    const days = waitingDays(r.requestedAt);
                    return (
                      <tr key={r.id} className="border-t border-[var(--border-subtle)] align-top">
                        <td className="px-6 py-4">
                          <div className="font-semibold text-[var(--text-primary)]">{r.name || 'No name'}</div>
                          <div className="text-[12px] text-[var(--text-muted)]">
                            {[r.phone, r.email].filter(Boolean).join(' · ') || `user ${shortId(r.userId)}`}
                          </div>
                        </td>
                        <td className="px-6 py-4 text-[13px] text-[var(--text-secondary)]">{sourceText(r)}</td>
                        <td className="px-6 py-4 text-[13px] text-[var(--text-muted)]">
                          {formatTimestamp(r.requestedAt)}
                          {r.status === 'pending' && (
                            <div className={cn(days >= 7 ? 'font-semibold text-[var(--state-danger)]' : '')}>
                              {days === 0 ? 'today' : `${days} day${days === 1 ? '' : 's'} waiting`}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 text-[13px]">
                          {r.status === 'pending' ? (
                            r.blocker ? (
                              <span className="inline-flex items-start gap-1.5 text-[var(--state-warning)]">
                                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" strokeWidth={1.5} />
                                Blocked
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-[var(--state-success)]">
                                <CheckCircle2 className="h-3.5 w-3.5" strokeWidth={1.5} /> Clear
                              </span>
                            )
                          ) : (
                            <>
                              <span className={cn('rounded-full px-2.5 py-1 text-[12px] font-semibold', deletionStatusChip[r.status])}>
                                {humanizeCode(r.status)}
                              </span>
                              <div className="mt-1 text-[11px] text-[var(--text-muted)]">
                                {formatTimestamp(r.reviewedAt ?? r.cancelledAt)}
                              </div>
                            </>
                          )}
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button
                            type="button"
                            onClick={() => open(r)}
                            className="rounded-xl bg-[var(--bg-base)] px-3 py-1.5 text-[13px] font-semibold text-[var(--text-primary)] transition-colors hover:bg-[var(--bg-elevated,var(--bg-base))]"
                          >
                            {r.status === 'pending' && canDecide ? 'Review' : 'View'}
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ── Review ─────────────────────────────────────────────────────── */}
      {active && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/55 p-4">
          <div className="my-8 w-full max-w-lg rounded-3xl bg-[var(--bg-surface)] shadow-lift">
            <div className="border-b border-[var(--border-subtle)] px-6 py-5">
              <h2 className="font-display text-[20px] font-medium tracking-[-0.02em] text-[var(--text-primary)]">
                {active.name || 'No name'}
              </h2>
              <p className="mt-1 text-[13px] text-[var(--text-muted)]">
                {[active.phone, active.email].filter(Boolean).join(' · ') || `user ${shortId(active.userId)}`}
              </p>
            </div>

            <div className="space-y-5 px-6 py-5">
              <div className="rounded-2xl bg-[var(--bg-base)] p-4 text-[13px]">
                <dl className="grid grid-cols-[auto,1fr] gap-x-4 gap-y-1.5">
                  <dt className="text-[var(--text-muted)]">From</dt>
                  <dd className="text-[var(--text-primary)]">{sourceText(active)}</dd>
                  <dt className="text-[var(--text-muted)]">Requested</dt>
                  <dd className="text-[var(--text-primary)]">{formatTimestamp(active.requestedAt)}</dd>
                  <dt className="text-[var(--text-muted)]">Account was</dt>
                  <dd className="text-[var(--text-primary)]">{humanizeCode(active.previousStatus)} before the lock</dd>
                  <dt className="text-[var(--text-muted)]">Status</dt>
                  <dd>
                    <span className={cn('rounded-full px-2 py-0.5 text-[12px] font-semibold', deletionStatusChip[active.status])}>
                      {humanizeCode(active.status)}
                    </span>
                  </dd>
                  {active.reviewedAt && (
                    <>
                      <dt className="text-[var(--text-muted)]">Decided</dt>
                      <dd className="text-[var(--text-primary)]">
                        {formatTimestamp(active.reviewedAt)} by {shortId(active.reviewedBy)}
                      </dd>
                    </>
                  )}
                  {active.cancelledAt && (
                    <>
                      <dt className="text-[var(--text-muted)]">Withdrawn</dt>
                      <dd className="text-[var(--text-primary)]">{formatTimestamp(active.cancelledAt)}</dd>
                    </>
                  )}
                </dl>
                {active.reason && (
                  <>
                    <p className="mb-1 mt-4 font-semibold text-[var(--text-secondary)]">Their reason</p>
                    <p className="text-[var(--text-primary)]">{active.reason}</p>
                  </>
                )}
                {active.decisionNote && (
                  <>
                    <p className="mb-1 mt-4 font-semibold text-[var(--text-secondary)]">Decision note</p>
                    <p className="text-[var(--text-primary)]">{active.decisionNote}</p>
                  </>
                )}
              </div>

              {active.status === 'pending' && active.blocker && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-warning-bg)] px-4 py-3">
                  <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-warning)]" strokeWidth={1.5} />
                  <div className="text-[13px] text-[var(--state-warning)]">
                    <p className="font-semibold">Cannot be approved yet</p>
                    <p className="mt-0.5">{active.blocker}</p>
                    <p className="mt-1.5 text-[12px] opacity-80">
                      This is the message the holder sees. Reject with a note telling them to sort it out,
                      or wait if it will clear by itself (a delivery about to finish, say).
                    </p>
                  </div>
                </div>
              )}

              {error && (
                <div className="flex items-start gap-2 rounded-2xl bg-[var(--state-danger-soft,rgba(220,38,38,0.08))] px-4 py-3">
                  <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-[var(--state-danger)]" strokeWidth={1.5} />
                  <p className="text-[13px] text-[var(--state-danger)]">{error}</p>
                </div>
              )}

              {active.status === 'pending' && canDecide && (
                <>
                  <div>
                    <label className="mb-1.5 block text-[13px] font-medium text-[var(--text-secondary)]">
                      Note <span className="text-[var(--text-muted)]">(required to reject; the holder sees it)</span>
                    </label>
                    <textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      rows={3}
                      maxLength={2000}
                      placeholder="e.g. Your wallet still holds KES 450. Withdraw it, then ask again."
                      className="w-full resize-none rounded-xl bg-[var(--bg-base)] px-3 py-2 text-[14px] text-[var(--text-primary)] outline-none"
                    />
                  </div>
                  {!active.blocker && (
                    <label className="flex items-start gap-2 text-[13px] text-[var(--text-secondary)]">
                      <input
                        type="checkbox"
                        checked={confirmed}
                        onChange={(e) => setConfirmed(e.target.checked)}
                        className="mt-0.5"
                      />
                      I understand approving erases this person&apos;s name, phone and email for good. Order and
                      payment records are kept for tax.
                    </label>
                  )}
                </>
              )}
              {active.status === 'pending' && !canDecide && (
                <p className="text-[13px] text-[var(--text-muted)]">
                  Deciding needs the users.delete permission.
                </p>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[var(--border-subtle)] px-6 py-4">
              <button
                type="button"
                onClick={() => setActive(null)}
                className="rounded-xl px-4 py-2 text-[13px] font-semibold text-[var(--text-secondary)] hover:bg-[var(--bg-base)]"
              >
                Close
              </button>
              {active.status === 'pending' && canDecide && (
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={busy || !note.trim()}
                    onClick={() => rejectM.mutate({ id: active.id, n: note.trim() })}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--bg-base)] px-4 py-2 text-[13px] font-semibold text-[var(--text-primary)] disabled:opacity-40"
                  >
                    {rejectM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <XCircle className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    Reject and unlock
                  </button>
                  <button
                    type="button"
                    disabled={busy || !!active.blocker || !confirmed}
                    onClick={() => approveM.mutate({ id: active.id, n: note.trim() })}
                    className="inline-flex items-center gap-1.5 rounded-xl bg-[var(--state-danger)] px-4 py-2 text-[13px] font-semibold text-white disabled:opacity-40"
                  >
                    {approveM.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" strokeWidth={1.5} /> : <Trash2 className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    Approve and delete
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </PageGuard>
  );
};
